import {
  Box,
  Collapse,
  Flex,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tooltip,
  useToken,
} from "@chakra-ui/react";
import EmojiPicker, { type EmojiClickData, EmojiStyle, Theme } from "emoji-picker-react";
import { useContext, useRef, useState } from "react";
import { VscReactions } from "react-icons/vsc";
import { useIntl } from "react-intl";

import { MessagesInner, ReactionEmojiRead } from "@nens/rana/api-v1";
import { RanaButton, TenantContext } from "@nens/ranalib";
import { useRect } from "@nens/react/hooks";

import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";

interface MessageReactionsProps {
  message: MessagesInner;
  onToggleReaction: (emoji: string) => void;
}

const QUICK_REACTIONS = ["👍", "👎", "🎉", "✅", "👀", "💡", "❓", "⚠️"];

export const MessageReactions = ({ message, onToggleReaction }: MessageReactionsProps) => {
  const [baseRounded] = useToken("radii", ["base"]);
  const quickReactionsRef = useRef<HTMLDivElement>(null);
  const { width: quickReactionsWidth } = useRect(quickReactionsRef);
  const { user, isPublic: isPublicViewer } = useContext(TenantContext);
  const { canWriteProject } = useProjectActionAllowed();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isFullPickerOpen, setIsFullPickerOpen] = useState(false);
  const intl = useIntl();

  const isReactionFromCurrentUser = (reaction: ReactionEmojiRead) => {
    // Public viewers can see reactions but cannot own or remove one
    if (isPublicViewer) return false;

    return (
      reaction.users?.some(
        (reactionUser) => reactionUser.id === user.id || reactionUser.email === user.email
      ) ?? false
    );
  };

  const closePicker = () => {
    setIsPickerOpen(false);
    setIsFullPickerOpen(false);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onToggleReaction(emojiData.emoji);
    closePicker();
  };

  const reactions = message.reactions ?? [];
  const reactionLabel = intl.formatMessage({
    id: "comment.add_reaction",
    defaultMessage: "Add reaction",
  });
  const moreEmojisLabel = intl.formatMessage({
    id: "comment.more_emojis",
    defaultMessage: "Show all emojis",
  });

  return (
    <Box position="relative" mt={1} onClick={(event) => event.stopPropagation()}>
      <Flex gap={1} flexWrap="wrap" alignItems="center">
        {reactions.map((reaction) => {
          const isOwnReaction = isReactionFromCurrentUser(reaction);
          const label = (reaction.users ?? []).map((user) => {
            const name = user.givenName + " " + user.familyName;
            return <Box key={user.id}>{name}</Box>;
          });
          return (
            <Tooltip key={reaction.emoji} label={label}>
              <RanaButton
                size="xs"
                variant="outline"
                borderColor={isOwnReaction ? "brand.deep_sea" : undefined}
                bg={isOwnReaction ? "brand.glacier" : undefined}
                cursor={canWriteProject ? undefined : "default"}
                onClick={canWriteProject ? () => onToggleReaction(reaction.emoji) : undefined}
                aria-label={`${reaction.emoji} ${reaction.users?.length ?? 0}`}
              >
                {reaction.emoji} {reaction.users?.length ?? 0}
              </RanaButton>
            </Tooltip>
          );
        })}
        {canWriteProject && (
          <Popover isOpen={isPickerOpen} onClose={closePicker} placement="bottom-start" closeOnBlur>
            <PopoverTrigger>
              <IconButton
                size="xs"
                variant="ghost"
                aria-label={reactionLabel}
                title={reactionLabel}
                icon={<VscReactions size="20px" />}
                onClick={() => {
                  setIsFullPickerOpen(false);
                  setIsPickerOpen((isOpen) => !isOpen);
                }}
              />
            </PopoverTrigger>
            <Portal>
              <PopoverContent width="sm" p={0} borderWidth={0} bg="transparent" zIndex="popover">
                <Collapse in={isFullPickerOpen}>
                  <EmojiPicker
                    theme={Theme.LIGHT}
                    emojiStyle={EmojiStyle.NATIVE}
                    skinTonesDisabled
                    onEmojiClick={handleEmojiClick}
                    previewConfig={{ showPreview: false }}
                    style={{ borderRadius: baseRounded }}
                    width={`${quickReactionsWidth}px`}
                  />
                </Collapse>
                <Flex
                  ref={quickReactionsRef}
                  gap={2}
                  align="center"
                  p={2}
                  bg="white"
                  borderWidth="base"
                  borderColor="brand.glacier"
                  rounded="base"
                  boxShadow="md"
                >
                  {QUICK_REACTIONS.map((emoji) => (
                    <RanaButton
                      key={emoji}
                      size="sm"
                      variant="ghost"
                      fontSize="xl"
                      aria-label={emoji}
                      onClick={() => handleEmojiClick({ emoji } as EmojiClickData)}
                    >
                      {emoji}
                    </RanaButton>
                  ))}
                  <IconButton
                    size="sm"
                    variant="outline"
                    aria-label={moreEmojisLabel}
                    title={moreEmojisLabel}
                    icon={<VscReactions size="20px" />}
                    onClick={() => setIsFullPickerOpen(true)}
                  />
                </Flex>
              </PopoverContent>
            </Portal>
          </Popover>
        )}
      </Flex>
    </Box>
  );
};
